// Cápsulas de autocapacitación en el panel: el reproductor, la sección
// "Aprende Tabularis" y el botón "Cómo se usa" de cada pantalla.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cargarCatalogo, paraRol, duracionTexto, vistas, marcarVista, CAPSULA_DE_VISTA, type Capsula, type Catalogo } from '../utils/capsulas';

const VELOCIDADES = [1, 1.25, 1.5];

/** Video en una ventana sobre la pantalla. Los subtítulos ya vienen en el
 *  video. Sin descarga ni menú del botón derecho: no impide copiarlo, pero no
 *  lo regala. */
export const Reproductor: React.FC<{ capsula: Capsula; siguiente?: Capsula | null; onSiguiente?: () => void; onCerrar: () => void; onTermino?: () => void }> = ({ capsula, siguiente, onSiguiente, onCerrar, onTermino }) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [vel, setVel] = useState(1);
  const [termino, setTermino] = useState(false);
  useEffect(() => { setTermino(false); }, [capsula.num]);
  useEffect(() => { if (ref.current) ref.current.playbackRate = vel; }, [vel, capsula.num]);
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onCerrar(); };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, [onCerrar]);
  return (
    <div role="dialog" aria-label={capsula.titulo} onClick={(e) => { if (e.target === e.currentTarget) onCerrar(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(5,12,26,0.82)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: 'min(1100px, 100%)', background: '#0D1B35', borderRadius: 14, overflow: 'hidden', boxShadow: '0 24px 70px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', color: '#E8EDF5' }}>
          <span style={{ fontSize: 12, color: '#8FB0E6', fontWeight: 600, whiteSpace: 'nowrap' }}>Cápsula {capsula.num}</span>
          <span style={{ fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{capsula.titulo}</span>
          <div role="group" aria-label="Velocidad" style={{ display: 'flex', gap: 4 }}>
            {VELOCIDADES.map((v) => (
              <button key={v} onClick={() => setVel(v)} aria-pressed={vel === v}
                style={{ border: '1px solid rgba(255,255,255,0.2)', background: vel === v ? '#E8EDF5' : 'transparent', color: vel === v ? '#0D1B35' : '#E8EDF5', borderRadius: 6, padding: '3px 8px', fontSize: 12, cursor: 'pointer' }}>
                {String(v).replace('.', ',')}x
              </button>
            ))}
          </div>
          <button onClick={onCerrar} aria-label="Cerrar" style={{ background: 'none', border: 'none', color: '#E8EDF5', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ position: 'relative', background: '#000' }}>
          <video ref={ref} key={capsula.video} src={capsula.video} poster={capsula.poster} controls autoPlay playsInline
            controlsList="nodownload noplaybackrate" disablePictureInPicture onContextMenu={(e) => e.preventDefault()}
            onLoadedMetadata={() => { if (ref.current) ref.current.playbackRate = vel; }}
            onEnded={() => { setTermino(true); onTermino?.(); }}
            style={{ width: '100%', display: 'block', aspectRatio: '16 / 9' }} />
          {termino && siguiente && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(13,27,53,0.86)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, color: '#E8EDF5', textAlign: 'center', padding: 20 }}>
              <div style={{ fontSize: 13, color: '#8FB0E6', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Siguiente cápsula</div>
              <div style={{ fontSize: 22, fontWeight: 600 }}>{siguiente.titulo}</div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="p-btn-primary" onClick={onSiguiente}>Verla →</button>
                <button onClick={() => { setTermino(false); ref.current?.play(); }} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.3)', color: '#E8EDF5', borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}>Ver de nuevo</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/** Sección "Aprende Tabularis": las rutas con sus cápsulas. Primero las de tu rol. */
export const Aprende: React.FC<{ uid: string; rol: string }> = ({ uid, rol }) => {
  const [cat, setCat] = useState<Catalogo | null>(null);
  const [error, setError] = useState(false);
  const [abierta, setAbierta] = useState<Capsula | null>(null);
  const [todas, setTodas] = useState(false);
  const [vistasSet, setVistas] = useState<Set<string>>(() => vistas(uid));
  useEffect(() => { cargarCatalogo().then(setCat).catch(() => setError(true)); }, []);

  const lista = useMemo(() => (cat ? cat.capsulas.filter((c) => todas || paraRol(c, rol)) : []), [cat, rol, todas]);
  const rutas = useMemo(() => {
    const m = new Map<number, Capsula[]>();
    lista.forEach((c) => m.set(c.ruta, [...(m.get(c.ruta) || []), c]));
    return [...m.entries()].sort((a, b) => a[0] - b[0]);
  }, [lista]);
  const siguienteDe = (c: Capsula) => { const i = lista.findIndex((x) => x.num === c.num); return i >= 0 ? lista[i + 1] || null : null; };
  const vistasDeLista = lista.filter((c) => vistasSet.has(c.num)).length;
  // La primera que falta, en el orden de las rutas: quien parte ve la Bienvenida.
  const sugerida = lista.find((c) => !vistasSet.has(c.num));

  if (error) return <div className="p-empty">Las cápsulas no están disponibles en este momento. Intenta de nuevo más tarde.</div>;
  if (!cat) return <div className="p-loading">Cargando cápsulas…</div>;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <div className="p-section-title" style={{ marginBottom: 4 }}>🎓 Aprende Tabularis</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Videos cortos, de una tarea cada uno. Llevas {vistasDeLista} de {lista.length}.</div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}>
          <input type="checkbox" checked={todas} onChange={(e) => setTodas(e.target.checked)} /> Ver también las de otros roles
        </label>
      </div>
      <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 3, marginBottom: 20 }}>
        <div style={{ height: 6, width: `${lista.length ? (vistasDeLista / lista.length) * 100 : 0}%`, background: '#2E8B57', borderRadius: 3, transition: 'width .4s' }} />
      </div>
      {sugerida && (
        <button onClick={() => setAbierta(sugerida)} style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left', background: 'rgba(39,86,168,0.18)', border: '1px solid rgba(143,176,230,0.35)', borderRadius: 12, padding: 12, marginBottom: 24, cursor: 'pointer', color: 'inherit' }}>
          <img src={sugerida.poster} alt="" style={{ width: 160, aspectRatio: '16/9', objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, color: '#8FB0E6', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{vistasDeLista ? 'Sigue con' : 'Empieza por aquí'}</div>
            <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--white)', margin: '2px 0' }}>{sugerida.titulo}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{sugerida.resumen} · {duracionTexto(sugerida.duracion)}</div>
          </div>
        </button>
      )}
      {rutas.map(([n, caps]) => (
        <section key={n} style={{ marginBottom: 26 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 16, color: 'var(--white)' }}>Ruta {n} · {cat.rutas[n]?.titulo}</h3>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{cat.rutas[n]?.descripcion}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
            {caps.map((c) => (
              <button key={c.num} onClick={() => setAbierta(c)} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 0, textAlign: 'left', cursor: 'pointer', overflow: 'hidden', color: 'inherit' }}>
                <div style={{ position: 'relative' }}>
                  <img src={c.poster} alt="" loading="lazy" style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }} />
                  <span style={{ position: 'absolute', right: 6, bottom: 6, background: 'rgba(0,0,0,0.75)', color: '#fff', fontSize: 11, borderRadius: 4, padding: '1px 6px' }}>{duracionTexto(c.duracion)}</span>
                  {vistasSet.has(c.num) && <span style={{ position: 'absolute', left: 6, top: 6, background: '#2E8B57', color: '#fff', fontSize: 11, borderRadius: 4, padding: '1px 6px' }}>✓ Vista</span>}
                </div>
                <div style={{ padding: '8px 10px 10px' }}>
                  <div style={{ fontSize: 11, color: '#8FB0E6' }}>{c.num}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)' }}>{c.titulo}</div>
                </div>
              </button>
            ))}
          </div>
        </section>
      ))}
      {abierta && (
        <Reproductor capsula={abierta} siguiente={siguienteDe(abierta)} onCerrar={() => setAbierta(null)}
          onSiguiente={() => setAbierta(siguienteDe(abierta))}
          onTermino={() => { marcarVista(uid, abierta.num); setVistas(vistas(uid)); }} />
      )}
    </div>
  );
};

/** Botón de la barra superior: abre la cápsula de la pantalla en que estás. */
export const BotonComoSeUsa: React.FC<{ vista: string; uid: string }> = ({ vista, uid }) => {
  const [cat, setCat] = useState<Catalogo | null>(null);
  const [abierta, setAbierta] = useState(false);
  useEffect(() => { cargarCatalogo().then(setCat).catch(() => {}); }, []);
  const capsula = cat?.capsulas.find((c) => c.num === CAPSULA_DE_VISTA[vista]);
  if (!capsula) return null;
  return (
    <>
      <button onClick={() => setAbierta(true)} title={`Video: ${capsula.titulo}`} className="panel-como-se-usa"
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid rgba(27,58,107,0.25)', borderRadius: 999, padding: '5px 11px', fontSize: 12.5, color: 'var(--cream)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
        <span aria-hidden>▶</span><span className="panel-como-se-usa-texto">Cómo se usa</span>
      </button>
      {abierta && <Reproductor capsula={capsula} onCerrar={() => setAbierta(false)} onTermino={() => marcarVista(uid, capsula.num)} />}
    </>
  );
};
